import { ArtworkIconTheme } from './components/ArtworkImage';
import { pushPageHistory } from './components/useModalHistory';
import { useState, useEffect, useCallback, useRef } from 'react';
import type { ReactNode } from 'react';
import { AuthDialog } from './components/AuthDialog';
import { DisplaySettingsView } from './components/DisplaySettingsView';
import { gameApi, InvestigatorDrawType, AncientOneSelectionType, AuthResponse, GameState, UserRole, UserLanguage, UserSettings } from './api';
import {
  CommonHeader,
  LoadingIndicator,
  GameView,
  SoloView,
  MultiplayerView,
  HomeView,
  AdminGamesView,
  AdminStatePerformanceView,
  PlayerSettingsView,
  WikiView,
  AchievementsView,
} from './components';
import './App.css';
import './EntryUI.css';
import { HeaderRoute } from './components/CommonHeader';
import { AboutView } from './components/AboutView';
import { GameErrorBoundary } from './components/GameErrorBoundary';
import { AuthPanel, AuthFlow } from './components/AuthPanel';
import { getStoredOtherWorldsBelowMap, setStoredOtherWorldsBelowMap, getStoredMapStyle, setStoredMapStyle, getStoredMonsterTokenStyle, setStoredMonsterTokenStyle } from './utils/clientSettings';
import { useSettingsAutosave } from './utils/useSettingsAutosave';
import { useNewActionsSession } from './utils/useNewActionsSession';

const PLAYER_NAME_KEY = 'boardgame_player_name';
const AUTH_TOKEN_KEY = 'boardgame_auth_token';
const AUTH_REFRESH_TOKEN_KEY = 'boardgame_auth_refresh';
const AUTH_ROLE_KEY = 'boardgame_auth_role';
const AUTH_LANGUAGE_KEY = 'boardgame_auth_language';
const GUEST_ID_KEY = 'boardgame_guest_id';
type ParsedRoute = { mode: HeaderRoute; gameId?: string; openSettings?: boolean };

const parseStoredLanguage = (value: string | null): UserLanguage => {
  if (value === 'Ru') {
    return 'Ru';
  }
  return 'En';
};

const generateGuestId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 10)}-${Math.random().toString(16).slice(2, 10)}`;
};

const parseRoute = (): ParsedRoute => {
  const path = window.location.pathname;
  if (path.startsWith('/game/')) {
    const id = path.substring('/game/'.length);
    if (id) return { mode: 'game', gameId: id };
  }
  if (path === '/admin/games') {
    return { mode: 'admin-games' };
  }
  if (path === '/admin/performance') {
    return { mode: 'admin-performance' };
  }
  if (path === '/settings') {
    return { mode: 'home', openSettings: true };
  }
  if (path === '/wiki') {
    return { mode: 'wiki' };
  }
  if (path === '/achievements' || path === '/achievements/') {
    return { mode: 'achievements' };
  }
  if (path === '/solo') {
    return { mode: 'solo' };
  }
  if (path === '/about') {
    return { mode: 'about' };
  }
  if (path === '/multiplayer') {
    return { mode: 'multiplayer' };
  }
  return { mode: 'home' };
};

function App() {
  const initialRoute = parseRoute();
  const [authAccessToken, setAuthAccessToken] = useState<string | null>(() => localStorage.getItem(AUTH_TOKEN_KEY));
  const [authRefreshToken, setAuthRefreshToken] = useState<string | null>(() => localStorage.getItem(AUTH_REFRESH_TOKEN_KEY));
  const [authUserName, setAuthUserName] = useState<string>(() => localStorage.getItem(PLAYER_NAME_KEY) || '');
  const [guestUserId, setGuestUserId] = useState<string | null>(() => localStorage.getItem(GUEST_ID_KEY));
  const [authRole, setAuthRole] = useState<UserRole | null>(() => (localStorage.getItem(AUTH_ROLE_KEY) as UserRole | null) || null);
  const [authLanguage, setAuthLanguage] = useState<UserLanguage>(() => parseStoredLanguage(localStorage.getItem(AUTH_LANGUAGE_KEY)));
  const [authFlow, setAuthFlow] = useState<AuthFlow | null>(null);
  const [authNotice, setAuthNotice] = useState('');
  const authFlowId = useRef(0);
  const authFlowRef = useRef<AuthFlow | null>(null);
  const [focusMultiplayerHeading, setFocusMultiplayerHeading] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const [authOtherWorldsBelowMap, setAuthOtherWorldsBelowMap] = useState(getStoredOtherWorldsBelowMap);
  const [authMapStyle, setAuthMapStyle] = useState(() => getStoredMapStyle());
  const [authMonsterTokenStyle, setAuthMonsterTokenStyle] = useState(() => getStoredMonsterTokenStyle());
  const [sendNotificationsToTelegram, setSendNotificationsToTelegram] = useState<boolean>(false);
  const [telegramId, setTelegramId] = useState<string>('');
  const [sendNotificationsToEmail, setSendNotificationsToEmail] = useState<boolean>(false);
  const [email, setEmail] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string>('');
  const [currentGameId, setCurrentGameId] = useState<string | undefined>(() => initialRoute.gameId);
  const [multiplayerPlayersCount, setMultiplayerPlayersCount] = useState<number>(1);
  const [multiplayerMaxInvestigators, setMultiplayerMaxInvestigators] = useState<number>(1);
  const [multiplayerSeed, setMultiplayerSeed] = useState<string>('');
  const [investigatorDrawType, setInvestigatorDrawType] = useState<InvestigatorDrawType>('Selection');
  const [ancientOneSelectionType, setAncientOneSelectionType] = useState<AncientOneSelectionType>('Random');
  const [enableDunwichBoard, setEnableDunwichBoard] = useState(true);
  const [enableKingsportBoard, setEnableKingsportBoard] = useState(true);
  const [enableInnsmouthBoard, setEnableInnsmouthBoard] = useState(true);
  const [includeDunwichCards, setIncludeDunwichCards] = useState<boolean>(true);
  const [includeSoloDarkPharaohCards, setIncludeSoloDarkPharaohCards] = useState<boolean>(true);
  const [includeKingsportCards, setIncludeKingsportCards] = useState<boolean>(true);
  const [includeKingInYellowCards, setIncludeKingInYellowCards] = useState<boolean>(false);
  const [includeBlackGoatCards, setIncludeBlackGoatCards] = useState<boolean>(false);
  const [includeLurkerCards, setIncludeLurkerCards] = useState<boolean>(false);
  const [includeInsmouthCards, setIncludeInsmouthCards] = useState<boolean>(false);
  const [usePersonalStories, setUsePersonalStories] = useState<boolean>(true);
  const [useNewGateMarkers, setUseNewGateMarkers] = useState<boolean>(true);
  const [includeMiskatonicCards, setIncludeMiskatonicCards] = useState<boolean>(false);
  const [includeFanMadeInvestigators, setIncludeFanMadeInvestigators] = useState<boolean>(false);
  const [randomCluePlacement, setRandomCluePlacement] = useState<boolean>(false);
  const [isGamePrivate, setIsGamePrivate] = useState<boolean>(false);
  const [soloInvestigators, setSoloInvestigators] = useState<number>(3);
  const [headerRoute, setRouteMode] = useState<HeaderRoute>(initialRoute.mode);
  const [settingsPopup, setSettingsPopup] = useState<'account' | 'display' | null>(initialRoute.openSettings ? 'account' : null);
  const isSettingsOpen = settingsPopup !== null;
  const settingsFocusRef = useRef<HTMLElement | null>(null);
  const [soloSeed, setSoloSeed] = useState<string>('');
  const isAuthenticated = !!authAccessToken;
  const newActionsSession = useNewActionsSession(`${isAuthenticated}:${authUserName}:${authRole}`);
  const isGuestSession = !isAuthenticated;
  const effectivePlayerName = isAuthenticated ? authUserName.trim() : (guestUserId?.trim() || '');

  const applySettings = (updated: UserSettings) => {
    setAuthLanguage(updated.language);
    setAuthMonsterTokenStyle(updated.monsterTokenStyle);
    setAuthOtherWorldsBelowMap(updated.otherWorldsBelowMap ?? false);
    if (updated.mapStyle) setAuthMapStyle(updated.mapStyle);
    setSendNotificationsToTelegram(updated.sendNotificationsToTelegram);
    setTelegramId(updated.telegramId ?? '');
    setSendNotificationsToEmail(updated.sendNotificationsToEmail);
    setEmail(updated.email ?? '');
    localStorage.setItem(AUTH_LANGUAGE_KEY, updated.language);
  };
  const settingsAutosave = useSettingsAutosave({
    otherWorldsBelowMap: authOtherWorldsBelowMap,
    language: authLanguage, monsterTokenStyle: authMonsterTokenStyle, mapStyle: authMapStyle,
    sendNotificationsToTelegram, telegramId: telegramId || null,
    sendNotificationsToEmail, email: email || null,
  }, settings => gameApi.updatePlayerSettings(settings), applySettings);
  useEffect(() => {
    setStoredMonsterTokenStyle(settingsAutosave.settings.monsterTokenStyle);
  }, [settingsAutosave.settings.monsterTokenStyle]);
  useEffect(() => {
    setStoredMapStyle(settingsAutosave.settings.mapStyle ?? 'Reworked');
  }, [settingsAutosave.settings.mapStyle]);
  useEffect(() => { setStoredOtherWorldsBelowMap(settingsAutosave.settings.otherWorldsBelowMap ?? false); }, [settingsAutosave.settings.otherWorldsBelowMap]);
  const settingsAutosaveRef = useRef(settingsAutosave);
  settingsAutosaveRef.current = settingsAutosave;

  const clearAuth = useCallback(() => {
    settingsAutosaveRef.current.reset();
    setAuthAccessToken(null);
    setAuthRefreshToken(null);
    setAuthUserName('');
    setAuthRole(null);
    setAuthLanguage('En');
    setAuthMonsterTokenStyle('Digital');
    setAuthMapStyle('Reworked');
    setAuthOtherWorldsBelowMap(false);
    authFlowRef.current = null;
    setAuthFlow(null);
    setSendNotificationsToTelegram(false);
    setTelegramId('');
    setSendNotificationsToEmail(false);
    setEmail('');
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(AUTH_REFRESH_TOKEN_KEY);
    localStorage.removeItem(PLAYER_NAME_KEY);
    localStorage.removeItem(AUTH_ROLE_KEY);
    localStorage.removeItem(AUTH_LANGUAGE_KEY);
    setStoredMonsterTokenStyle('Digital');
    setStoredMapStyle('Reworked');
    setStoredOtherWorldsBelowMap(false);
    gameApi.setAuthTokens(null, null);
  }, []);

  const handleAuthUpdated = useCallback((auth: AuthResponse | null) => {
    if (auth) {
      setAuthAccessToken(auth.accessToken);
      setAuthRefreshToken(auth.refreshToken);
      setAuthUserName(auth.userName);
      setAuthRole(auth.role);
      setAuthLanguage(auth.language);
      setAuthOtherWorldsBelowMap(auth.otherWorldsBelowMap ?? false);
      if (auth.mapStyle) {
        setAuthMapStyle(auth.mapStyle);
        setStoredMapStyle(auth.mapStyle);
      }
      if (auth.monsterTokenStyle) {
        setAuthMonsterTokenStyle(auth.monsterTokenStyle);
        setStoredMonsterTokenStyle(auth.monsterTokenStyle);
      }
      setSendNotificationsToTelegram(false);
      setTelegramId('');
      setSendNotificationsToEmail(false);
      setEmail('');
      localStorage.setItem(AUTH_TOKEN_KEY, auth.accessToken);
      localStorage.setItem(AUTH_REFRESH_TOKEN_KEY, auth.refreshToken);
      localStorage.setItem(PLAYER_NAME_KEY, auth.userName);
      localStorage.setItem(AUTH_ROLE_KEY, auth.role);
      localStorage.setItem(AUTH_LANGUAGE_KEY, auth.language);
    } else {
      clearAuth();
    }
  }, [clearAuth]);

  useEffect(() => {
    gameApi.setAuthTokens(authAccessToken, authRefreshToken, handleAuthUpdated);
  }, [authAccessToken, authRefreshToken, handleAuthUpdated]);

  useEffect(() => {
    if (isAuthenticated) {
      gameApi.setGuestUserId(guestUserId);
      return;
    }

    const normalizedGuestId = guestUserId?.trim();
    if (!normalizedGuestId) {
      const generatedGuestId = generateGuestId();
      setGuestUserId(generatedGuestId);
      gameApi.setGuestUserId(generatedGuestId);
      return;
    }

    gameApi.setGuestUserId(normalizedGuestId);
  }, [guestUserId, isAuthenticated]);

  const clampCount = useCallback((value: number) => Math.min(8, Math.max(1, value || 1)), []);

  const applyAuthResponse = useCallback((auth: AuthResponse) => {
    gameApi.setAuthTokens(auth.accessToken, auth.refreshToken, handleAuthUpdated);
    setAuthAccessToken(auth.accessToken);
    setAuthRefreshToken(auth.refreshToken);
    setAuthUserName(auth.userName);
    setAuthRole(auth.role);
    setAuthLanguage(auth.language);
    setAuthOtherWorldsBelowMap(auth.otherWorldsBelowMap ?? false);
    if (auth.mapStyle) {
      setAuthMapStyle(auth.mapStyle);
      setStoredMapStyle(auth.mapStyle);
    }
    if (auth.monsterTokenStyle) {
      setAuthMonsterTokenStyle(auth.monsterTokenStyle);
      setStoredMonsterTokenStyle(auth.monsterTokenStyle);
    }
    setSendNotificationsToTelegram(false);
    setTelegramId('');
    setSendNotificationsToEmail(false);
    setEmail('');
    localStorage.setItem(AUTH_TOKEN_KEY, auth.accessToken);
    localStorage.setItem(AUTH_REFRESH_TOKEN_KEY, auth.refreshToken);
    localStorage.setItem(PLAYER_NAME_KEY, auth.userName);
    localStorage.setItem(AUTH_ROLE_KEY, auth.role);
    localStorage.setItem(AUTH_LANGUAGE_KEY, auth.language);
  }, [handleAuthUpdated]);

  const goToGame = useCallback((gameId: string) => {
    setCurrentGameId(gameId);
    pushPageHistory(`/game/${gameId}`);
    setRouteMode('game');
  }, []);

  const openAuth = useCallback((step: 'login' | 'signup', destination?: 'multiplayer') => {
    setAuthNotice('');
    const flow: AuthFlow = { id: ++authFlowId.current, step, username: '', opener: document.activeElement as HTMLElement | null, destination };
    authFlowRef.current = flow;
    setAuthFlow(flow);
  }, []);
  const openSignupPopup = useCallback(() => openAuth('signup'), [openAuth]);
  const openLoginPopup = useCallback(() => openAuth('login'), [openAuth]);
  const cancelAuth = useCallback(() => {
    authFlowRef.current = null;
    setAuthFlow(null);
  }, []);
  const completeAuth = useCallback((flow: AuthFlow) => {
    if (authFlowRef.current !== flow) return;
    authFlowRef.current = null;
    setAuthFlow(null);
    if (flow.destination === 'multiplayer') {
      setRouteMode('multiplayer');
      pushPageHistory('/multiplayer');
      setFocusMultiplayerHeading(true);
    }
  }, []);

  const handleLogout = useCallback(async () => {
    try {
      if (authRefreshToken) {
        await gameApi.logout(authRefreshToken);
      }
    } catch (err) {
      // ignore logout errors
    } finally {
      clearAuth();
      setMessage('Logged out');
      setRouteMode('home');
      setSettingsPopup(null);
      window.location.href = '/';
      window.location.reload();
    }
  }, [authRefreshToken, clearAuth, setRouteMode]);

  const navigateTo = useCallback((mode: HeaderRoute) => {
    authFlowRef.current = null;
    setAuthFlow(null);
    setRouteMode(mode);
    if (mode === 'home') {
      pushPageHistory('/');
    } else if (mode === 'solo') {
      pushPageHistory('/solo');
    } else if (mode === 'multiplayer') {
      pushPageHistory('/multiplayer');
    } else if (mode === 'admin-games') {
      pushPageHistory('/admin/games');
    } else if (mode === 'admin-performance') {
      pushPageHistory('/admin/performance');
    } else if (mode === 'wiki') {
      pushPageHistory('/wiki');
    } else if (mode === 'achievements') {
      pushPageHistory('/achievements');
    } else if (mode === 'about') {
      pushPageHistory('/about');
    }
  }, []);

  const openSettingsPopup = useCallback(() => {
    setSettingsError(null);
    setSettingsPopup('display');
  }, []);

  const closeSettingsPopup = useCallback(() => {
    void settingsAutosaveRef.current.flush();
    setSettingsPopup(null);
  }, []);

  const handleSelectGame = useCallback(
    async (gameId: string) => {
      if (!gameId) return;
      setLoading(true);
      setError(null);
      try {
        goToGame(gameId);
        setMessage('Switched to selected game.');
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to select game');
      } finally {
        setLoading(false);
      }
    },
    [goToGame]
  );

  const handleStartGame = async (
    seed?: number,
    drawType: InvestigatorDrawType = investigatorDrawType,
    ancientType: AncientOneSelectionType = ancientOneSelectionType,
    includeDunwich: boolean = includeDunwichCards,
    includeDarkPharaohCards: boolean = includeSoloDarkPharaohCards,
    useKingsportCards: boolean = includeKingsportCards,
    useRandomCluePlacement: boolean = randomCluePlacement,
    useFanMadeInvestigators: boolean = includeFanMadeInvestigators,
    useKingInYellowCards: boolean = includeKingInYellowCards,
    useBlackGoatCards: boolean = includeBlackGoatCards,
    useInsmouthCards: boolean = includeInsmouthCards,
    useStories: boolean = usePersonalStories,
    useLurkerCards: boolean = includeLurkerCards
  ) => {
    const effectiveName = effectivePlayerName;
    if (!effectiveName) {
      setError('Set a player name first');
      return;
    }
    const playersCount = clampCount(multiplayerPlayersCount);
    if (playersCount > 1 && !isAuthenticated) {
      setError('Login is required to start a multiplayer game');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const startArguments = [
        seed,
        playersCount,
        undefined,
        drawType,
        ancientType,
        includeDunwich,
        effectiveName,
        isGamePrivate,
        includeDarkPharaohCards,
        useKingsportCards,
        useRandomCluePlacement,
      ] as const;
      const result = await gameApi.startGame(
        ...startArguments,
        useFanMadeInvestigators,
        useKingInYellowCards,
        useBlackGoatCards,
        useInsmouthCards,
        useInsmouthCards && useStories,
        useLurkerCards || undefined,
        useLurkerCards ? useNewGateMarkers : undefined,
        includeMiskatonicCards,
        enableDunwichBoard,
        enableKingsportBoard,
        enableInnsmouthBoard,
        Math.max(playersCount, clampCount(multiplayerMaxInvestigators))
      );
      await gameApi.joinGame(effectiveName, result.gameId);
      goToGame(result.gameId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleStartSoloGame = async () => {
    const effectiveName = effectivePlayerName;
    if (!effectiveName) {
      setError('Set a player name first');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const seedValue = soloSeed.trim() === '' ? undefined : Number(soloSeed);
      const soloCount = clampCount(soloInvestigators);
      const includeDarkPharaohCards = includeSoloDarkPharaohCards;
      const useKingsportCards = includeKingsportCards;
      const startArguments = [
        seedValue,
        1,
        soloCount,
        investigatorDrawType,
        ancientOneSelectionType,
        includeDunwichCards,
        effectiveName,
        isGamePrivate,
        includeDarkPharaohCards,
        useKingsportCards,
        randomCluePlacement,
      ] as const;
      const result = await gameApi.startGame(
        ...startArguments,
        includeFanMadeInvestigators,
        includeKingInYellowCards,
        includeBlackGoatCards,
        includeInsmouthCards,
        includeInsmouthCards && usePersonalStories,
        includeLurkerCards || undefined,
        includeLurkerCards ? useNewGateMarkers : undefined,
        includeMiskatonicCards,
        enableDunwichBoard,
        enableKingsportBoard,
        enableInnsmouthBoard
      );
      goToGame(result.gameId);
      await gameApi.joinGame(effectiveName, result.gameId);
      setMessage('Solo game created and started.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create solo game');
    } finally {
      setLoading(false);
    }
  };

  const handleStartMultiplayerGame = async () => {
    const seedValue = multiplayerSeed.trim() === '' ? undefined : Number(multiplayerSeed);
    const includeDarkPharaohCards = includeSoloDarkPharaohCards;
    const useKingsportCards = includeKingsportCards;
    await handleStartGame(
      seedValue,
      investigatorDrawType,
      ancientOneSelectionType,
      includeDunwichCards,
      includeDarkPharaohCards,
      useKingsportCards,
      randomCluePlacement,
      includeFanMadeInvestigators,
      includeKingInYellowCards,
      includeBlackGoatCards,
      includeInsmouthCards,
      usePersonalStories
    );
  };

  const handleJoinGame = async () => {
    const effectiveName = effectivePlayerName;
    if (!effectiveName) {
      setError('Set a player name first');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const joinResult = await gameApi.joinGame(effectiveName, currentGameId);
      if (joinResult.gameId) {
        goToGame(joinResult.gameId);
      }
      setMessage('Successfully joined the game!');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to join game');
    } finally {
      setLoading(false);
    }
  };

  const handleExecuteAction = async (actionId: string): Promise<GameState | null> => {
    setLoading(true);
    setError(null);
    try {
      return await gameApi.executeAction(
        actionId,
        currentGameId,
        effectivePlayerName || undefined
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const loadPlayerSettings = useCallback(async () => {
    if (!isAuthenticated) {
      return;
    }
    const effectiveName = authUserName.trim();
    if (!effectiveName) {
      return;
    }

    if (settingsAutosaveRef.current.hasDraft()) return;
    const revision = settingsAutosaveRef.current.getRevision();
    setSettingsError(null);
    try {
      const settings = await gameApi.getPlayerSettings(effectiveName);
      if (settingsAutosaveRef.current.hasDraft() || settingsAutosaveRef.current.getRevision() !== revision) return;
      setAuthLanguage(settings.language);
      setAuthMonsterTokenStyle(settings.monsterTokenStyle);
      setAuthOtherWorldsBelowMap(settings.otherWorldsBelowMap ?? false);
      if (settings.mapStyle) setAuthMapStyle(settings.mapStyle);
      setSendNotificationsToTelegram(settings.sendNotificationsToTelegram);
      setTelegramId(settings.telegramId ?? '');
      setSendNotificationsToEmail(settings.sendNotificationsToEmail);
      setEmail(settings.email ?? '');
      setStoredMonsterTokenStyle(settings.monsterTokenStyle);
    } catch (err) {
      if (settingsAutosaveRef.current.getRevision() !== revision) return;
      setSettingsError(err instanceof Error ? err.message : 'Failed to load player settings');
    }
  }, [authUserName, isAuthenticated]);

  const retrySettings = async () => {
    if (settingsAutosave.hasDraft()) return settingsAutosave.retry();
    await loadPlayerSettings();
    return true;
  };

  const renderAppLayout = (content: ReactNode) => (
    <div className={`App ${headerRoute === 'game' ? 'game-route' : ''} ${headerRoute === 'home' ? 'home-route' : ''} ${headerRoute === 'achievements' ? 'achievements-route' : ''}`}>
      <ArtworkIconTheme />
      <div className="app-shell">
        <CommonHeader
          playerName={effectivePlayerName}
          isGuestSession={isGuestSession}
          activeRoute={headerRoute}
          onNavigateHome={() => navigateTo('home')}
          onNavigateToWiki={() => navigateTo('wiki')}
          onNavigateToAchievements={() => navigateTo('achievements')}
          onNavigateToAbout={() => navigateTo('about')}
          onOpenSettings={openSettingsPopup}
          onOpenAccount={() => { setSettingsError(null); setSettingsPopup('account'); }}
          onOpenSignUp={openSignupPopup}
          onOpenLogin={openLoginPopup}
        />
        {content}
        <div className="auth-outcome-status" role="status" aria-live="polite">{authNotice}</div>
        {authFlow && <AuthPanel key={authFlow.id} flow={authFlow} guestUserId={guestUserId}
          isCurrent={() => authFlowRef.current === authFlow}
          onAuthenticated={(auth, registered) => {
            applyAuthResponse(auth);
            if (registered) { setGuestUserId(null); gameApi.setGuestUserId(null); }
            setMessage(registered ? 'Signed up and logged in' : 'Logged in');
            setAuthNotice(registered ? 'Account created. You are logged in.' : 'You are logged in.');
          }}
          onSettingsUpdated={updated => {
            if (localStorage.getItem(PLAYER_NAME_KEY) !== authFlow.username.trim()) return;
            setAuthLanguage(updated.language);
            setAuthMonsterTokenStyle(updated.monsterTokenStyle);
            setAuthOtherWorldsBelowMap(updated.otherWorldsBelowMap ?? false);
            if (updated.mapStyle) setAuthMapStyle(updated.mapStyle);
            setSendNotificationsToTelegram(updated.sendNotificationsToTelegram);
            setTelegramId(updated.telegramId ?? '');
            setSendNotificationsToEmail(updated.sendNotificationsToEmail);
            setEmail(updated.email ?? '');
            localStorage.setItem(AUTH_LANGUAGE_KEY, updated.language);
            setStoredMonsterTokenStyle(updated.monsterTokenStyle);
            setAuthNotice('Telegram notifications enabled.');
          }}
          onComplete={() => completeAuth(authFlow)} onCancel={cancelAuth} />}
        {isSettingsOpen && isAuthenticated && <AuthDialog
          title={settingsPopup === 'display' ? 'Settings' : 'Account'}
          closeLabel={settingsPopup === 'display' ? 'Close settings' : 'Close account'}
          className="preferences-dialog" initialFocusRef={settingsFocusRef} onClose={closeSettingsPopup}>
          {settingsPopup === 'display' ? <>
            <DisplaySettingsView {...settingsAutosave.settings}
              onSettingsChange={patch => { setSettingsError(null); settingsAutosave.change(patch); }} />
            <div className="settings-save-status" role="status" aria-live="polite">
              {settingsAutosave.status === 'saving' ? 'Saving…' : settingsAutosave.status === 'saved' ? 'Saved.' : ''}
            </div>
            {(settingsAutosave.error || settingsError) && <div className="error" role="alert">
              {settingsAutosave.error || settingsError} <button type="button" onClick={() => { void retrySettings(); }}>Retry</button>
            </div>}
          </> : <PlayerSettingsView
            playerName={authUserName} {...settingsAutosave.settings}
            saving={settingsAutosave.status === 'saving'} saved={settingsAutosave.status === 'saved'}
            error={settingsAutosave.error || settingsError}
            onSettingsChange={(patch, debounce) => { setSettingsError(null); settingsAutosave.change(patch, debounce); }}
            onSettingsFlush={settingsAutosave.flush} onSettingsRetry={retrySettings}
            onLogout={handleLogout} onClose={closeSettingsPopup} inPopup />}
        </AuthDialog>}

      </div>
    </div>
  );

  const renderGameContent = () => {
    return (
      <GameErrorBoundary key={currentGameId}>
        <GameView
          currentGameId={currentGameId}
          openedActionSessions={newActionsSession.openedSessions}
          loading={loading}
          playerName={effectivePlayerName}
          guestUserId={isGuestSession ? guestUserId : null}
          authRole={authRole || ''}
          userLanguage={authLanguage}
          message={message}
          onStartGame={handleStartGame}
          onJoinGame={handleJoinGame}
          onExecuteAction={handleExecuteAction}
          onGameIdChange={goToGame}
          onMessage={setMessage}
          onError={setError}
        />
      </GameErrorBoundary>
    );
  };

  useEffect(() => {
    const onPop = () => {
      authFlowRef.current = null;
      setAuthFlow(null);
      const parsed = parseRoute();
      setRouteMode(parsed.mode);
      setCurrentGameId(parsed.gameId);
      setSettingsPopup(parsed.openSettings ? 'account' : null);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  useEffect(() => {
    if (!focusMultiplayerHeading) return;
    if (headerRoute === 'multiplayer' && !authFlow) {
      const frame = requestAnimationFrame(() => {
        document.getElementById('multiplayer-setup-heading')?.focus();
        setFocusMultiplayerHeading(false);
      });
      return () => cancelAnimationFrame(frame);
    }
    setFocusMultiplayerHeading(false);
  }, [focusMultiplayerHeading, headerRoute, authFlow]);

  useEffect(() => {
    loadPlayerSettings();
  }, [loadPlayerSettings]);

  useEffect(() => {
    if (isSettingsOpen && !isAuthenticated) {
      setSettingsPopup(null);
      return;
    }

    if (isSettingsOpen) {
      loadPlayerSettings();
    }
  }, [isAuthenticated, isSettingsOpen, loadPlayerSettings]);

  if (headerRoute === 'home') {
    return renderAppLayout(
      <HomeView
        playerName={effectivePlayerName}
        authRole={authRole}
        currentGameId={currentGameId}
        isAuthenticated={isAuthenticated}
        onNavigateToMultiplayer={() => {
          if (!isAuthenticated) {
            openAuth('login', 'multiplayer');
            return;
          }
          navigateTo('multiplayer');
        }}
        onNavigateToSolo={() => navigateTo('solo')}
        onSelectGame={handleSelectGame}
        onOpenAdminGames={() => navigateTo('admin-games')}
        onOpenStatePerformance={() => navigateTo('admin-performance')}
        onAuthChanged={applyAuthResponse}
        onMessage={setMessage}
      />
    );
  }

  if (headerRoute === 'wiki') {
    return renderAppLayout(
      <WikiView
        onBack={() => navigateTo('home')}
        initialLanguage={authLanguage}
      />
    );
  }

  if (headerRoute === 'achievements') {
    return renderAppLayout(
      <AchievementsView />
    );
  }

  if (headerRoute === 'about') {
    return renderAppLayout(
      <AboutView />
    );
  }

  if (headerRoute === 'admin-games') {
    return renderAppLayout(
      <AdminGamesView
        playerName={authUserName}
        authRole={authRole}
        isAuthenticated={isAuthenticated}
        onBack={() => navigateTo('home')}
        onSelectGame={handleSelectGame}
      />
    );
  }

  if (headerRoute === 'admin-performance') {
    return renderAppLayout(
      <AdminStatePerformanceView
        authRole={authRole}
        isAuthenticated={isAuthenticated}
        onBack={() => navigateTo('home')}
      />
    );
  }

  if (headerRoute === 'solo') {
    return renderAppLayout(
        <SoloView
          soloInvestigators={soloInvestigators}
          soloSeed={soloSeed}
          investigatorDrawType={investigatorDrawType}
          ancientOneSelectionType={ancientOneSelectionType}
          enableDunwichBoard={enableDunwichBoard}
          enableKingsportBoard={enableKingsportBoard}
          enableInnsmouthBoard={enableInnsmouthBoard}
          onEnableDunwichBoardChange={setEnableDunwichBoard}
          onEnableKingsportBoardChange={setEnableKingsportBoard}
          onEnableInnsmouthBoardChange={setEnableInnsmouthBoard}
          includeDunwichCards={includeDunwichCards}
          includeDarkPharaohCards={includeSoloDarkPharaohCards}
          includeKingsportCards={includeKingsportCards}
          includeKingInYellowCards={includeKingInYellowCards}
          includeBlackGoatCards={includeBlackGoatCards}
          includeMiskatonicCards={includeMiskatonicCards}
          includeLurkerCards={includeLurkerCards}
          includeInsmouthCards={includeInsmouthCards}
          usePersonalStories={usePersonalStories}
          useNewGateMarkers={useNewGateMarkers}
          includeFanMadeInvestigators={includeFanMadeInvestigators}
          randomCluePlacement={randomCluePlacement}
          showAdminOnly={authRole === 'Admin'}
          loading={loading}
          error={error}
          onInvestigatorsChange={value => setSoloInvestigators(clampCount(value))}
          onSeedChange={setSoloSeed}
          onDrawTypeChange={setInvestigatorDrawType}
          onAncientOneSelectionTypeChange={setAncientOneSelectionType}
          onIncludeDunwichCardsChange={setIncludeDunwichCards}
          onIncludeDarkPharaohCardsChange={setIncludeSoloDarkPharaohCards}
          onIncludeKingsportCardsChange={setIncludeKingsportCards}
          onIncludeKingInYellowCardsChange={setIncludeKingInYellowCards}
          onIncludeBlackGoatCardsChange={setIncludeBlackGoatCards}
          onIncludeMiskatonicCardsChange={setIncludeMiskatonicCards}
          onIncludeLurkerCardsChange={setIncludeLurkerCards}
          onIncludeInsmouthCardsChange={setIncludeInsmouthCards}
          onUsePersonalStoriesChange={setUsePersonalStories}
          onUseNewGateMarkersChange={setUseNewGateMarkers}
          onIncludeFanMadeInvestigatorsChange={setIncludeFanMadeInvestigators}
          onRandomCluePlacementChange={setRandomCluePlacement}
          onCreate={handleStartSoloGame}
          onBack={() => navigateTo('home')}
          isPrivate={isGamePrivate}
          onPrivateChange={setIsGamePrivate}
        />
      );
    }

  if (headerRoute === 'multiplayer') {
    if (!isAuthenticated) {
      return renderAppLayout(
        <div className="home-menu">
          <h2>Multiplayer</h2>
          <div className="error">Login is required to access multiplayer.</div>
          <button onClick={() => navigateTo('home')}>Back</button>
        </div>
      );
    }

    return renderAppLayout(
        <MultiplayerView
          playersCount={multiplayerPlayersCount}
          maxInvestigatorsCount={multiplayerMaxInvestigators}
          onInvestigatorsCountChange={value => setMultiplayerMaxInvestigators(Math.max(multiplayerPlayersCount, clampCount(value)))}
          seed={multiplayerSeed}
          investigatorDrawType={investigatorDrawType}
          ancientOneSelectionType={ancientOneSelectionType}
          enableDunwichBoard={enableDunwichBoard}
          enableKingsportBoard={enableKingsportBoard}
          enableInnsmouthBoard={enableInnsmouthBoard}
          onEnableDunwichBoardChange={setEnableDunwichBoard}
          onEnableKingsportBoardChange={setEnableKingsportBoard}
          onEnableInnsmouthBoardChange={setEnableInnsmouthBoard}
          includeDunwichCards={includeDunwichCards}
          includeDarkPharaohCards={includeSoloDarkPharaohCards}
          includeKingsportCards={includeKingsportCards}
          includeKingInYellowCards={includeKingInYellowCards}
          includeBlackGoatCards={includeBlackGoatCards}
          includeMiskatonicCards={includeMiskatonicCards}
          includeLurkerCards={includeLurkerCards}
          includeInsmouthCards={includeInsmouthCards}
          usePersonalStories={usePersonalStories}
          useNewGateMarkers={useNewGateMarkers}
          includeFanMadeInvestigators={includeFanMadeInvestigators}
          randomCluePlacement={randomCluePlacement}
          showAdminOnly={authRole === 'Admin'}
          loading={loading}
          error={error}
          onPlayersCountChange={value => {
            const count = clampCount(value);
            setMultiplayerPlayersCount(count);
            setMultiplayerMaxInvestigators(current => Math.max(current, count));
          }}
          onSeedChange={setMultiplayerSeed}
          onDrawTypeChange={setInvestigatorDrawType}
          onAncientOneSelectionTypeChange={setAncientOneSelectionType}
          onIncludeDunwichCardsChange={setIncludeDunwichCards}
          onIncludeDarkPharaohCardsChange={setIncludeSoloDarkPharaohCards}
          onIncludeKingsportCardsChange={setIncludeKingsportCards}
          onIncludeKingInYellowCardsChange={setIncludeKingInYellowCards}
          onIncludeBlackGoatCardsChange={setIncludeBlackGoatCards}
          onIncludeMiskatonicCardsChange={setIncludeMiskatonicCards}
          onIncludeLurkerCardsChange={setIncludeLurkerCards}
          onIncludeInsmouthCardsChange={setIncludeInsmouthCards}
          onUsePersonalStoriesChange={setUsePersonalStories}
          onUseNewGateMarkersChange={setUseNewGateMarkers}
          onIncludeFanMadeInvestigatorsChange={setIncludeFanMadeInvestigators}
          onRandomCluePlacementChange={setRandomCluePlacement}
          onCreate={handleStartMultiplayerGame}
          onBack={() => navigateTo('home')}
          isPrivate={isGamePrivate}
          onPrivateChange={setIsGamePrivate}
        />
      );
    }

  return renderAppLayout(
    <div className="game-screen">
      {renderGameContent()}

      {loading && <LoadingIndicator />}
    </div>
  );
}

export default App;
